/**
 * -----------------------------------------------------------------------------
 * Vehicle Dynamic Input Labels
 * -----------------------------------------------------------------------------
 * Pure helpers for the Dynamic Input Labels section of the vehicle admin page.
 * The list of labels comes from the API (each with its input_number and level),
 * so nothing here knows how many inputs an asset has. Labels can be set or
 * cleared, never added or removed. The rules match the dynamic_pto_label table:
 * up to 64 characters, blank allowed. Surrounding whitespace is trimmed, as the
 * API does.
 */
import { VehicleDynamicPtoLabel } from "../../../api/types/Vehicle";

export const DYNAMIC_INPUT_LABEL_MAX_LENGTH = 64;

// what the user has typed so far, by label_number; untouched fields are absent
export type EditedDynamicInputLabels = Partial<Record<number, string>>;

// a label counts as set when it has any text
export const setDynamicInputLabelCount = (
  storedLabels: VehicleDynamicPtoLabel[]
): number =>
  storedLabels.filter((storedLabel) => storedLabel.label.trim() !== "").length;

const storedDynamicInputLabelText = (
  storedLabels: VehicleDynamicPtoLabel[],
  labelNumber: number
): string =>
  storedLabels.find((storedLabel) => storedLabel.label_number === labelNumber)
    ?.label ?? "";

export const displayedDynamicInputLabelText = (
  storedLabels: VehicleDynamicPtoLabel[],
  editedLabels: EditedDynamicInputLabels,
  labelNumber: number
): string =>
  editedLabels[labelNumber] ??
  storedDynamicInputLabelText(storedLabels, labelNumber);

export const dynamicInputLabelIsTooLong = (labelText: string): boolean =>
  // counted in characters (code points), as the API and database count them,
  // so an emoji is one character rather than two UTF-16 units
  Array.from(labelText.trim()).length > DYNAMIC_INPUT_LABEL_MAX_LENGTH;

export const editedDynamicInputLabelsAreValid = (
  editedLabels: EditedDynamicInputLabels
): boolean =>
  Object.values(editedLabels).every(
    (labelText) => !dynamicInputLabelIsTooLong(labelText ?? "")
  );

// the edited labels whose trimmed text differs from what is stored, ready to save
export const changedDynamicInputLabels = (
  storedLabels: VehicleDynamicPtoLabel[],
  editedLabels: EditedDynamicInputLabels
): { label_number: number; label: string }[] =>
  storedLabels.flatMap((storedLabel) => {
    const editedLabelText = editedLabels[storedLabel.label_number];
    if (editedLabelText === undefined) return [];
    const trimmedLabelText = editedLabelText.trim();
    return trimmedLabelText === storedLabel.label
      ? []
      : [{ label_number: storedLabel.label_number, label: trimmedLabelText }];
  });
